import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Collapse } from 'reactstrap';

// Helpers
import { isEmptyString, validEmail } from 'Shared/helpers';

// Types
import { WorkspaceType } from 'Shared/constants/types';

// Utils
import { useAddTeammate } from 'Shared/utils/hooks/use-add-teammate';
import { useCopyToClipboard } from 'Shared/utils/hooks/use-copy-to-clipboard';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useMembers } from 'Shared/utils/hooks';

// Selectors
import { getMembersWhoHaveNotTeammateYet } from 'Shared/selectors/members';

// Components
import Select from './Select/Select';
import SimpleAddWorkspaceWrapper from '../SimpleAddWorkspaceWrapper/SimpleAddWorkspaceWrapper';
import { Illustrations } from 'Wpds/components/illustration';
import TextField from 'Shared/components/TextField/TextField';

// Styles
import './SimpleAddTeammateModal.scss';
import { WpIcon } from 'Wpds/components/icon';

type Props = {
  onClose: Function;
};

function emailValidation(value) {
  if (value && !validEmail(value)) return `Invalid email addres:  ${value}`;
  return undefined;
}

function SimpleAddTeammateModal({ onClose }: Props) {
  const { user } = useCurrentUser();
  const inviteLink = user.inviteLink;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isLoading: isLoadingMembers } = useMembers();

  const members = useSelector(getMembersWhoHaveNotTeammateYet);

  const { formValues, onTextFieldsChange, disabledSubmit, isInvalidEmail, onSubmit, setFormValues } = useAddTeammate({
    isSubmitting,
    setIsSubmitting,
  });
  const emailOptions = members?.map(({ id, fullName, email }) => ({ id, email, fullName }));

  const { onCopyTextToClipboard } = useCopyToClipboard({ textToCopy: inviteLink });

  const copyInviteLink = () => {
    (window as any).analytics.track('Copied Invite Link', { is_group: false });
    onCopyTextToClipboard();
  };

  const handleSelectChange = useCallback(
    value => {
      (window as any).analytics.track('Clicked in Add Workspace Modal', {
        modal: 'add_teammate',
        button: 'Meeting',
      });

      setFormValues({ ...formValues, email: value });
    },
    [formValues, setFormValues]
  );

  const isShowPersonalMessage = !isInvalidEmail() && !isEmptyString(formValues.email);

  return (
    <SimpleAddWorkspaceWrapper
      onClose={onClose}
      workspaceType={WorkspaceType.isTeammate}
      title="Add 1:1 Workspace"
      description="Who are you creating the workspace for?"
      disabledSubmit={disabledSubmit}
      isSubmitting={isSubmitting}
      onSubmit={onSubmit}
      createNameSection={
        <div className="d-flex justify-content-between flex-column flex-sm-row align-items-end mt-3">
          <div className="w-100 mr-0 mr-sm-2">
            <TextField
              name="firstName"
              type="text"
              label="First Name"
              labelMuted={false}
              placeholder="First Name"
              value={formValues.firstName}
              onChange={onTextFieldsChange}
            />
          </div>
          <TextField
            name="lastName"
            type="text"
            labelMuted={false}
            label="Last Name"
            placeholder="Last Name"
            value={formValues.lastName}
            onChange={onTextFieldsChange}
          />
        </div>
      }
      optionToInviteSection={
        <form autoComplete="off">
          <Select
            value={formValues.email}
            options={emailOptions}
            onChange={handleSelectChange}
            validate={emailValidation}
            isLoading={isLoadingMembers}
          />
          <Collapse isOpen={isShowPersonalMessage}>
            {isShowPersonalMessage ? (
              <div className="d-flex flex-column">
                <div className="small font-weight-bold text-muted mb-1">Personal Message (optional)</div>
                <textarea
                  name="personalMessage"
                  className="w-100 border rounded p-2"
                  value={formValues.personalMessage}
                  onChange={onTextFieldsChange}
                />
              </div>
            ) : null}
          </Collapse>
          <div>
            or
            <button type="button" className="btn btn-link px-1" onClick={copyInviteLink}>
              <WpIcon name="link" /> copy invite link
            </button>
          </div>
          <div className="simple-add-teammate-modal-description d-none d-sm-block">
            Anyone with the same domain as you can join, but they won’t have access to private workspaces without being
            invited.
          </div>
        </form>
      }
      illustration={Illustrations.two_people_standing_with_laptop_purple_bg}
    />
  );
}

export default SimpleAddTeammateModal;
